<script setup>
import TrashIcon from '@iconify-vue/ph/trash';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import { toastDeleted, toastError } from '@js/utils/toast';
import { ref, watch } from 'vue';

const {
    url,
    config = {},
    resolveData,
    callback,
    title,
    content,
} = defineProps({
    url: {
        type: String,
        required: true,
    },
    config: {
        type: Object,
        required: false,
    },
    resolveData: {
        type: Function,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
    title: {
        type: String,
        required: false,
    },
    content: {
        type: String,
        required: false,
    },
});

const isLoading = ref(false);

const isResolving = ref(false);

const isOpen = ref(false);

const resolvedData = ref(null);

function deleteAction() {
    isLoading.value = true;

    const requestConfig = resolveData
        ? { ...config, data: { ...config.data, ...resolvedData.value } }
        : config;

    axios
        .delete(url, requestConfig)
        .then((response) => {
            isOpen.value = false;

            toastDeleted(response);

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isLoading.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}

watch(
    () => isOpen.value,
    (newValue) => {
        if (newValue && resolveData) {
            isResolving.value = true;
            resolvedData.value = null;

            Promise.resolve(resolveData())
                .then((data) => {
                    resolvedData.value = data;
                })
                .finally(() => {
                    isResolving.value = false;
                });
        }
    },
);
</script>

<template>
    <DialogModal v-model="isOpen" size="small" :loading="isResolving">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            <slot name="title" :data="resolvedData">
                {{ title ?? "Supprimer l'élément" }}
            </slot>
        </template>

        <template #content>
            <slot name="content" :data="resolvedData">
                {{
                    content ??
                    'Êtes-vous sûr de vouloir supprimer cet élément ?'
                }}
            </slot>
        </template>

        <template #footer>
            <div class="flex justify-end gap-2">
                <ActionButton
                    type="transparent"
                    :callback="() => (isOpen = false)"
                >
                    Annuler
                </ActionButton>

                <ActionButton
                    type="danger"
                    :left-icon="TrashIcon"
                    :callback="() => deleteAction()"
                    :loading="isLoading"
                >
                    Supprimer
                </ActionButton>
            </div>
        </template>
    </DialogModal>
</template>
